import _ from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import * as Sentry from "@sentry/react";

export type SaveStatus = "idle" | "saving" | "success" | "error";

interface AutoSaveContextProps {
  children: React.ReactNode;
}

type AutoSaveContextType = {
  autoSaveStatus: SaveStatus;
  startAutoSave: (saveFunction: () => Promise<void>) => void;
  flushAutoSave: () => Promise<void>;
  cancelAutoSave: () => void;
};

export const AutoSaveContext = React.createContext<AutoSaveContextType | null>(
  null
);

export const AutoSaveProvider: React.FC<AutoSaveContextProps> = ({
  children
}) => {
  const [autoSaveStatus, setAutoSaveStatus] =
    React.useState<SaveStatus>("idle");

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  type Deferred = {
    promise: Promise<void>;
    resolve: () => void;
    reject: (reason?: unknown) => void;
  };

  const pendingDeferredRef = useRef<Deferred | null>(null);
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);
  const inFlightIdRef = useRef<number | null>(null);
  const saveIdCounterRef = useRef(0);

  const createDeferred = (): Deferred => {
    let resolve!: () => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };

  const scheduleResetToIdle = (delayMs: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setAutoSaveStatus("idle");
    }, delayMs);
  };

  const debouncedAutoSave = useRef(
    _.debounce((saveFunction: () => Promise<void>, deferred: Deferred) => {
      saveIdCounterRef.current += 1;
      const saveId = saveIdCounterRef.current;
      inFlightIdRef.current = saveId;

      const promise = (async () => {
        try {
          await saveFunction();
          setAutoSaveStatus("success");
          scheduleResetToIdle(1000);
          deferred.resolve();
        } catch (error) {
          Sentry.captureException(error);
          setAutoSaveStatus("error");
          scheduleResetToIdle(3000);
          deferred.reject(error);
        } finally {
          if (inFlightIdRef.current === saveId) {
            inFlightIdRef.current = null;
            inFlightPromiseRef.current = null;
          }
          // If a new autosave was scheduled while this was running, keep it.
          if (pendingDeferredRef.current === deferred) {
            pendingDeferredRef.current = null;
          }
        }
      })();
      inFlightPromiseRef.current = promise;
    }, 3000)
  ).current;

  const startAutoSave = (saveFunction: () => Promise<void>) => {
    setAutoSaveStatus("saving");
    if (!pendingDeferredRef.current) {
      pendingDeferredRef.current = createDeferred();
    }
    debouncedAutoSave(saveFunction, pendingDeferredRef.current);
  };

  const flushAutoSave = useCallback(async () => {
    const pending = pendingDeferredRef.current?.promise ?? null;
    // Trigger the debounced call immediately if queued.
    debouncedAutoSave.flush();

    // Wait for queued save (if any), then any in-flight save.
    if (pending) {
      await pending;
    }
    if (inFlightPromiseRef.current) {
      await inFlightPromiseRef.current;
    }
  }, [debouncedAutoSave]);

  const cancelAutoSave = useCallback(() => {
    debouncedAutoSave.cancel();
    pendingDeferredRef.current = null;
    inFlightPromiseRef.current = null;
    inFlightIdRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setAutoSaveStatus("idle");
  }, [debouncedAutoSave]);

  // Cleanup on unmount: cancel pending debounce + clear status timer
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedAutoSave]);

  const value = useMemo(
    () => ({
      autoSaveStatus,
      startAutoSave,
      flushAutoSave,
      cancelAutoSave
    }),
    [autoSaveStatus, flushAutoSave, cancelAutoSave]
  );

  return (
    <AutoSaveContext.Provider value={value}>
      {children}
    </AutoSaveContext.Provider>
  );
};

export const useAutoSaveContext = () => {
  const context = useContext(AutoSaveContext);
  if (!context) {
    throw new Error("useAutoSave must be used within an AutoSaveProvider");
  }
  return context;
};
